import { useEffect, useState } from 'react';
import { Link, Prompt, Redirect } from 'react-router-dom';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Switch } from '@givebutter/primitives/switch';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { AuctionCategoriesDrawer } from 'components/pages/campaign-settings/campaign-auction/auction-categories/AuctionCategoriesDrawer';
import * as DeleteItem from 'components/pages/campaign-settings/campaign-auction/item-details/DeleteItem';
import * as ExtendAuctionDialog from 'components/pages/campaign-settings/campaign-auction/items-form/ExtendAuctionDialog';
import * as ItemDetailsForm from 'components/pages/campaign-settings/campaign-auction/items-form/ItemDetailsForm';
import { useItemForm } from 'components/pages/campaign-settings/campaign-auction/items-form/useItemForm';
import { didItemEnd } from 'components/pages/campaign-settings/campaign-auction/util';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';

const ItemForm = () => {
    const {
        item,
        isEditing,
        onSubmitItemForm,
        itemRoutes,
        formMethods,
        isDuplicating,
        showUnsavedItemPrompt,
        auction,
        campaignId,
        onDeleteItem,
        setIsDraft,
        isLoading,
        itemForm,
        hasCustomBiddingDateRange,
        setHasCustomBiddingDateRange,
        customMinDate,
    } = useItemForm();
    const { handleSubmit, setFocus, control, path } = formMethods;

    const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    useEffect(() => {
        setTimeout(() => setFocus('name'), 0);
    }, [setFocus]);

    if (!!item && didItemEnd(item)) {
        return <Redirect to={itemRoutes.show(item.id)} />;
    }

    const auctionEnded = dateFromServer(auction.end_at) < DateTime.now();
    const saveOptionsLabel = isEditing && itemForm.isPublished ? 'Save Changes' : 'Save & List';
    const canExtend = item?.can_extend;
    const didStart = !isDuplicating && !!item && !item.can_delete;

    return (
        <>
            <Drawer.Root open={isCategoriesOpen} onOpenChange={setIsCategoriesOpen}>
                <Drawer.Content>
                    <AuctionCategoriesDrawer
                        categoryOrderOnSuccess={() => setIsCategoriesOpen(false)}
                    />
                </Drawer.Content>
            </Drawer.Root>
            <Form.Provider {...formMethods}>
                <div className={'flex flex-col gap-y-8'}>
                    <div>
                        <Link
                            to={
                                isEditing && !isDuplicating && item?.id
                                    ? itemRoutes.show(item.id)
                                    : itemRoutes.index()
                            }
                            style={{ display: 'inline-block' }}
                        >
                            <Button variant="outline" color="neutral" size="sm">
                                <FontAwesomeIcon size={'sm'} icon={faChevronLeft} />
                                {isEditing && !isDuplicating ? 'Back to details' : 'Back to items'}
                            </Button>
                        </Link>
                    </div>
                    <Prompt
                        when={showUnsavedItemPrompt}
                        message={
                            'You have unfinished changes. If you leave this page, your item will not be duplicated.'
                        }
                    />
                    <Alerts />
                    <Box className={'divide-y divide-solid overflow-auto'}>
                        <form className={'contents'} onSubmit={handleSubmit(onSubmitItemForm)}>
                            <ItemDetailsForm.Root
                                onAddingFile={setIsUploadingImage}
                                onCategoriesOpen={setIsCategoriesOpen}
                            >
                                <div className={'flex flex-col gap-y-8 p-8'}>
                                    <ItemDetailsForm.Title />
                                    <ItemDetailsForm.Name />
                                    <ItemDetailsForm.SponsorDescription />
                                    <ItemDetailsForm.Categories />
                                    <ItemDetailsForm.Photos />
                                    <ItemDetailsForm.SponsorMedia />
                                </div>
                                <div className={'flex flex-col gap-y-8 p-8'}>
                                    <ItemDetailsForm.SellingFormat />
                                    <div className={'grid grid-cols-3 gap-6'}>
                                        <ItemDetailsForm.StartingPrice />
                                        <ItemDetailsForm.BuyNow />
                                        <ItemDetailsForm.ReservePrice />
                                        <ItemDetailsForm.ItemValue />
                                        <ItemDetailsForm.BidIncrement />
                                        <ItemDetailsForm.TotalQuantity />
                                        <ItemDetailsForm.Shipping />
                                    </div>
                                    <div className={'flex flex-col gap-y-4'}>
                                        <div className={'flex justify-between'}>
                                            <div className={'flex flex-col gap-y-2'}>
                                                <p className={'text-base font-medium'}>
                                                    Custom Bidding Date &amp; Time
                                                </p>
                                                <p className={'text-base text-gray-600'}>
                                                    Default bidding occurs between the start and end
                                                    of your auction.
                                                </p>
                                            </div>
                                            <Switch
                                                disabled={didStart}
                                                checked={hasCustomBiddingDateRange}
                                                onCheckedChange={checked =>
                                                    setHasCustomBiddingDateRange(checked as boolean)
                                                }
                                            />
                                        </div>
                                        <div
                                            className={`${!hasCustomBiddingDateRange ? 'opacity-60' : ''}`}
                                        >
                                            <Form.UseFormDateTimePicker
                                                control={control}
                                                name={'biddingDateRange'}
                                                disabledStart={!hasCustomBiddingDateRange}
                                                disabledEnd={!hasCustomBiddingDateRange}
                                                minDate={customMinDate}
                                                maxDate={dateFromServer(auction.end_at)}
                                            />
                                            <Form.Error name={path('biddingDateRange')} />
                                        </div>
                                    </div>
                                    {!auction.in_person && <ItemDetailsForm.InPerson />}
                                </div>
                                <ItemDetailsForm.Visibility />
                                <div className={'flex flex-col gap-x-2 gap-y-6 p-8'}>
                                    <ItemDetailsForm.FulfillmentDetails />
                                    <ItemDetailsForm.SponsorPurchaseMessage />
                                </div>
                                <div
                                    className={`flex flex-wrap-reverse gap-2 !border-none px-8 pb-8 ${
                                        isEditing ? 'justify-between' : 'justify-end'
                                    }`}
                                >
                                    {isEditing && item && (
                                        <DeleteItem.Root
                                            campaignId={campaignId}
                                            item={item}
                                            onDelete={onDeleteItem}
                                        >
                                            <DeleteItem.Trigger asChild={true}>
                                                <Button
                                                    variant="solid"
                                                    disabled={!item?.can_delete}
                                                    color={'error'}
                                                >
                                                    Delete
                                                </Button>
                                            </DeleteItem.Trigger>
                                        </DeleteItem.Root>
                                    )}
                                    <div className={'flex items-center gap-2'}>
                                        {(!!item && !isDuplicating ? item.can_delete : true) && (
                                            <Form.SubmitButton
                                                noAnimation={true}
                                                variant={'outline'}
                                                color={'neutral'}
                                                onClick={() => {
                                                    setIsDraft(true);
                                                }}
                                                disabled={isLoading || isUploadingImage}
                                            >
                                                {isEditing && !itemForm.isPublished
                                                    ? 'Keep as Draft'
                                                    : 'Save as Draft'}
                                            </Form.SubmitButton>
                                        )}
                                        {auctionEnded && canExtend ? (
                                            <ExtendAuctionDialog.Root
                                                onExtend={handleSubmit(onSubmitItemForm)}
                                            >
                                                <ExtendAuctionDialog.Trigger asChild>
                                                    <Button variant="solid" type="button">
                                                        Extend item
                                                    </Button>
                                                </ExtendAuctionDialog.Trigger>
                                            </ExtendAuctionDialog.Root>
                                        ) : (
                                            <Form.SubmitButton
                                                onClick={() => {
                                                    setIsDraft(false);
                                                }}
                                                disabled={isLoading || isUploadingImage}
                                            >
                                                {canExtend ? 'Extend item' : saveOptionsLabel}
                                            </Form.SubmitButton>
                                        )}
                                    </div>
                                </div>
                            </ItemDetailsForm.Root>
                        </form>
                    </Box>
                </div>
            </Form.Provider>
        </>
    );
};

const Alerts = () => {
    const { item, isDuplicating, sponsoredItem } = useItemForm();

    const canExtend = item?.can_extend;
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        <>
            {canExtend && (
                <AlertNotification.Root type="info" className="flex text-neutral-700">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>Review item details</AlertNotification.Title>
                        <AlertNotification.Description>
                            Before re-listing, review item's details.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}
            {isSponsored && (
                <AlertNotification.Root type="success" closable={true}>
                    Before listing, you can edit the details of your sponsored items, including the
                    starting price, buy-it-now price, and custom date and time.
                </AlertNotification.Root>
            )}
            {isDuplicating && (
                <AlertNotification.Root closable={true} type="success">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>Item duplicated!</AlertNotification.Title>
                        <AlertNotification.Description>
                            Review your item's details and "Save Changes" to list your items.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                    <AlertNotification.Close />
                </AlertNotification.Root>
            )}
        </>
    );
};

export { ItemForm };
